import { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import {
  meetingRequestTemplates,
  MeetingTemplateItem,
} from '@groove/api/gateway/v1/users';
import { useQuery } from 'react-query';
import { logSplunkError } from '@groove/api/analytics/splunk';
import { formatDuration, intervalToDuration } from 'date-fns';

const DEFAULT_LINK_TEXT = 'Book a meeting with me';

export type MargeFieldMeetingTypeOption = Pick<
  MeetingTemplateItem,
  'id' | 'name' | 'custom_link'
>;

interface UseNonSFDCSchedulerLinkMergeFieldOutput {
  isLoading: boolean;
  selectedMeetingType: string | null;
  meetingTypes: MargeFieldMeetingTypeOption[];
  customLinkLabel: string;
  handleClearSelectedMeetigType: () => void;
  handleCustomLinkLabelChange: ChangeEventHandler<HTMLInputElement>;
  handleMeetingTypeChange: ChangeEventHandler<HTMLInputElement>;
}

export const useNonSFDCSchedulerLinkMergeField =
  (): UseNonSFDCSchedulerLinkMergeFieldOutput => {
    const [meetingTypes, setMeetingTypes] = useState<
      MargeFieldMeetingTypeOption[]
    >([]);
    const [selectedMeetingType, setSelectedMeetingType] = useState<
      string | null
    >(null);
    const [customLinkLabel, setCustomLinkLabel] =
      useState<string>(DEFAULT_LINK_TEXT);

    const defaultOption: MargeFieldMeetingTypeOption = useMemo(
      () => ({
        id: -1,
        name: 'All (Default link)',
        custom_link: '',
      }),
      [],
    );

    const { isFetching, data: meetingTypesData } = useQuery(
      ['meeting-types'],
      () => meetingRequestTemplates(),
      {
        onError: (e: Error) => {
          logSplunkError(e);
        },
      },
    );

    const handleCustomLinkLabelChange: ChangeEventHandler<
      HTMLInputElement
    > = event => {
      setCustomLinkLabel(event.target.value);
    };

    const handleMeetingTypeChange: ChangeEventHandler<
      HTMLInputElement
    > = event => {
      setSelectedMeetingType(event.target.value);
    };

    const handleClearSelectedMeetigType = (): void => {
      setSelectedMeetingType(String(defaultOption.id));
    };

    useEffect(() => {
      if ((meetingTypes && meetingTypes?.length > 0) || !meetingTypesData)
        return;

      setSelectedMeetingType(String(defaultOption.id));

      const convertedMeetingTypesData: MargeFieldMeetingTypeOption[] =
        meetingTypesData
          ? meetingTypesData.map(({ id, name, duration, custom_link }) => {
              const durationObject = intervalToDuration({
                start: 0,
                end: duration * 1000,
              });
              const formattedDuration = formatDuration(durationObject);
              return {
                id,
                name: `${name} (${formattedDuration})`,
                custom_link,
              };
            })
          : [];
      setMeetingTypes([defaultOption, ...convertedMeetingTypesData]);
    }, [meetingTypesData, meetingTypes, defaultOption]);

    return {
      isLoading: isFetching,
      selectedMeetingType,
      meetingTypes,
      customLinkLabel,
      handleClearSelectedMeetigType,
      handleCustomLinkLabelChange,
      handleMeetingTypeChange,
    };
  };

export default useNonSFDCSchedulerLinkMergeField;
